<script setup lang="ts">
  // Read-only order preview (plan §5.4), opened via showDynamicDialog.
  // Renders the shared view tree in preview mode; "Abrir en Pedidos" converts
  // it into a regular view tab. Order resolution mirrors PosV2ViewWorkspace:
  // prefer the live ordersStore instance, fall back to a fetched snapshot.
  import { computed, onBeforeMount, ref, watch } from 'vue';
  import { IOrder } from '@/interfaces/IOrder';
  import OrdersService from '@/api/services/orders.service';
  import { useModal } from '@/composition/use-modal';
  import { useOrders } from '@/composition/use-orders';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosV2ViewWorkspaceBase from '@/components/pos-v2/view/PosV2ViewWorkspaceBase.vue';

  export interface IPosV2OrderPreviewResult {
    openedInOrders: boolean;
  }

  const props = defineProps<{
    orderId: number;
  }>();

  const MODAL_NAME = 'pos-v2-order-preview';

  const modals = useModal();
  const ordersHelper = useOrders();
  const ordersStore = useOrdersStore();
  const restaurantStore = useRestaurantStore();

  const storeOrder = computed<IOrder | null>(() => {
    const orders = ordersStore.orders;
    if (!orders) return null;
    return orders.find((o) => o.id === props.orderId) ?? null;
  });

  const providedOrder = ref<IOrder | null>(null);
  const loading = ref(false);

  watch(
    storeOrder,
    (o) => {
      if (o) providedOrder.value = o;
    },
    { immediate: true }
  );

  onBeforeMount(async () => {
    if (providedOrder.value) return;

    const restaurantId = restaurantStore.restaurant?.id;
    if (!restaurantId) return;

    loading.value = true;
    try {
      const fetched = await OrdersService.v2GetOrderById(
        restaurantId,
        props.orderId
      );
      // The store instance may have arrived (socket) while fetching; it wins.
      if (!providedOrder.value) providedOrder.value = fetched;
    } catch (e) {
      console.error('Error fetching order for preview', e);
    } finally {
      loading.value = false;
    }
  });

  const opening = ref(false);

  async function handleOpenInOrders() {
    if (opening.value || !providedOrder.value) return;

    opening.value = true;
    // openOrder routes to the V2 view tab (flag is active on POS pages) and
    // surfaces its own error toasts; it reports whether it actually opened.
    let opened = false;
    try {
      opened = await ordersHelper.openOrder(providedOrder.value);
    } catch (e) {
      console.error('Error opening order from preview', e);
    }

    // Renderer safety: only touch local state while the modal stays open;
    // dialogSubmit unmounts this component.
    if (!opened) {
      opening.value = false;
      return;
    }

    modals.dialogSubmit(MODAL_NAME, <IPosV2OrderPreviewResult>{
      openedInOrders: true,
    });
  }

  function handleClose() {
    modals.hide(MODAL_NAME);
  }
</script>

<template>
  <div class="pos-v2-order-preview-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ $t('posV2.orderPreviewTitle') }}</h2>
      <PosUiButton
        icon="close"
        color="neutral"
        variant="ghost"
        size="md"
        @click="handleClose"
      />
    </div>

    <div class="modal-body">
      <PosV2ViewWorkspaceBase
        :order="providedOrder"
        :loading="loading"
        mode="preview"
      />
    </div>

    <div class="modal-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        size="lg"
        class="btn-close"
        @click="handleClose"
      >
        {{ $t('posV2.close') }}
      </PosUiButton>

      <PosUiButton
        color="primary"
        variant="solid"
        size="lg"
        class="btn-open"
        :disabled="!providedOrder || opening"
        @click="handleOpenInOrders"
      >
        {{ $t('posV2.openInOrders') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Root lives inside .vm--modal.dialog--height-limit (a max-height flex
  // parent): the body asks for 76vh and shrinks on short screens.
  .pos-v2-order-preview-modal {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col;

    .modal-header {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-3;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }
    }

    .modal-body {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden;
      height: 76vh;
    }

    .modal-footer {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-200 tw-px-4 tw-py-3;

      :deep(.btn-close),
      :deep(.btn-open) {
        @apply tw-min-w-[150px];
      }
    }
  }
</style>
